import { create } from 'zustand';
import type { ObjectId, Vec2 } from '@/types';
import type { InteractionMode } from '@/core/interaction/pointerStateMachine';
import type { SnapGuide } from '@/core/interaction/snapping';

export interface ConnectPreview {
  readonly sourceId: ObjectId;
  readonly to: Vec2;
  readonly targetId: ObjectId | null;
}

/**
 * Transient, high-frequency interaction feedback: what is hovered, the current
 * gesture, live snap guides and the connect rubber band. Nothing here is persisted
 * or undoable. Consumers subscribe to single fields so a pointer move re-renders at
 * most the guide overlay and the two objects whose hover state changed.
 */
export interface InteractionStore {
  readonly mode: InteractionMode;
  readonly hoveredId: ObjectId | null;
  readonly guides: readonly SnapGuide[];
  readonly connect: ConnectPreview | null;
  /** Object briefly pulsed after a search/palette navigation. */
  readonly highlightedId: ObjectId | null;
  readonly setHighlighted: (id: ObjectId | null) => void;
  readonly setMode: (mode: InteractionMode) => void;
  readonly setHovered: (id: ObjectId | null) => void;
  readonly setGuides: (guides: readonly SnapGuide[]) => void;
  readonly setConnect: (preview: ConnectPreview | null) => void;
  readonly reset: () => void;
}

const NO_GUIDES: readonly SnapGuide[] = [];

export const useInteractionStore = create<InteractionStore>()((set, get) => ({
  mode: 'idle',
  hoveredId: null,
  guides: NO_GUIDES,
  connect: null,
  highlightedId: null,
  setHighlighted: (highlightedId) => {
    if (get().highlightedId !== highlightedId) set({ highlightedId });
  },
  setMode: (mode) => {
    if (get().mode !== mode) set({ mode });
  },
  setHovered: (hoveredId) => {
    if (get().hoveredId !== hoveredId) set({ hoveredId });
  },
  setGuides: (guides) => {
    if (guides.length === 0 && get().guides.length === 0) return;
    set({ guides: guides.length ? guides : NO_GUIDES });
  },
  setConnect: (connect) => {
    if (connect === null && get().connect === null) return;
    set({ connect });
  },
  reset: () => {
    set({ mode: 'idle', hoveredId: null, guides: NO_GUIDES, connect: null, highlightedId: null });
  },
}));
